import React, { useMemo } from "react";
import { Box, Text } from "@chakra-ui/react";
import Card from "components/card/Card";
import BarChart from "components/charts/BarChart";
import dayjs from "dayjs";

export default function InventoryProjectionGraph({
  curve,
  unit = "",
  interval = "weekly",
}) {
  /* ===============================
     1️⃣ Safe data extraction
  =============================== */
  const consumption = curve?.consumption ?? [];
  const remainingInventory = curve?.remaining_inventory ?? [];

  /* ===============================
     2️⃣ X-axis categories
  =============================== */
  const categories = useMemo(() => {
    if (interval === "daily") {
      return consumption.map(d =>
        dayjs(d.date).format("MM-DD")
      );
    }
    if (interval === "weekly") {
      return consumption.map(d => d.week);
    }
    if (interval === "monthly") {
      return consumption.map(d => d.month);
    }
    return [];
  }, [consumption, interval]);

  /* ===============================
     3️⃣ Series
  =============================== */
  const series = useMemo(() => {
  const consumptionValues = consumption.map(d =>
    Math.round(Number(d.value || 0))
  );

  const remainingMap = Object.fromEntries(
    remainingInventory.map(d => [
      interval === "weekly" ? d.week :
      interval === "monthly" ? d.month :
      d.date,
      Math.round(Number(d.value))
    ])
  );

  const remainingValues = categories.map(cat =>
    remainingMap[cat] ?? null
  );

  return [
    {
      name: "Forecast Consumption",
      type: "column",
      data: consumptionValues,
    },
    {
      name: "Projected Remaining",
      type: "line",
      data: remainingValues,
    },
  ];
}, [consumption, remainingInventory, categories, interval]);

  /* ===============================
     4️⃣ Dynamic scale (SEPARATED)
  =============================== */

  const safeAxis = (min, max) => {
      if (min === max) {
        if (min === 0) {
          return { min: 0, max: 1 };   // 🔥 핵심
        }
        return {
          min: min * 0.9,
          max: max * 1.1,
        };
      }
      return { min, max };
    };
  const {
    cMin,
    cMax,
    rMin,
    rMax,
  } = useMemo(() => {
    const cVals = series[0].data.filter(v => v != null);
    const rVals = series[1].data.filter(v => v != null);

    return {
      cMin: cVals.length ? Math.min(...cVals) : 0,
      cMax: cVals.length ? Math.max(...cVals) : 0,
      rMin: rVals.length ? Math.min(...rVals) : 0,
      rMax: rVals.length ? Math.max(...rVals) : 0,
    };
  }, [series]);

  // const cPadMin = Math.max(0, cMin * 0.9);
  // const cPadMax = cMax * 1.1;

  // const rPadMin = Math.max(0, rMin * 0.9);
  // const rPadMax = rMax * 1.1;
  const { min: cPadMin, max: cPadMax } = safeAxis(
    Math.max(0, cMin * 0.9),
    cMax * 1.1
  );

  const { min: rPadMin, max: rPadMax } = safeAxis(
    Math.max(0, rMin * 0.9),
    rMax * 1.1
  );
  /* ===============================
     5️⃣ Empty state
  =============================== */
  if (!consumption.length) {
    return (
      <Card bg="neutral.900" p="4">
        <Text color="#aaa" fontSize="sm">
          Inventory Projection ({interval})
        </Text>
        <Text color="#666" fontSize="sm" mt="2">
          No projection data.
        </Text>
      </Card>
    );
  }

  /* ===============================
     6️⃣ Chart options
  =============================== */
  const chartOptions = {
    chart: {
      type: "line",
      toolbar: { show: false },
      zoom: { enabled: false },
      background: "transparent",
      animations: { enabled: true, easing: "easeinout", speed: 400 },
    },

    plotOptions: {
      bar: {
        borderRadius: 6,
        columnWidth: "18%",
        endingShape: "rounded",
      },
    },

    stroke: {
      width: [0, 3],
      curve: "smooth",
      dashArray: [0, 6],
    },

    markers: {
      size: [0, 7],
      strokeWidth: 2,
      strokeColors: "#000",
    },

    grid: {
      borderColor: "#222",
      strokeDashArray: 5,
    },

    dataLabels: { enabled: false },

    xaxis: {
      categories,
      labels: {
        style: { fontSize: "11px", colors: "#aaa" },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },

    yaxis: [
      {
        // ⬅️ LEFT — Consumption
        seriesName: "Forecast Consumption",
        min: cPadMin,
        max: cPadMax,
        tickAmount: interval === "daily" ? 3 : 4,
        title: {
          text: `Forecast Consumption (${unit})`,
          style: { color: "#aaa", fontSize: "11px" },
        },
        labels: {
          formatter: v => Math.round(v),
          style: { colors: "#888", fontSize: "11px" },
        },
      },
      {
        // ➡️ RIGHT — Remaining
        seriesName: "Projected Remaining",
        opposite: true,
        min: rPadMin,
        max: rPadMax,
        tickAmount: interval === "daily" ? 3 : 4,
        title: {
          text: `Remaining Inventory (${unit})`,
          style: { color: "#aaa", fontSize: "11px" },
        },
        labels: {
          formatter: v => Math.round(v),
          style: { colors: "#888", fontSize: "11px" },
        },
      },
    ],

    legend: {
      position: "bottom",
      labels: { colors: "#bbb" },
      markers: { shape: "circle", size: 8 },
    },

    tooltip: {
      theme: "dark",
      shared: true,
      intersect: false, // ✅ 반드시 필요
      y: {
        formatter: (v) =>
          v == null ? "-" : `${Math.round(v).toLocaleString()} ${unit}`,
      },
    },

    colors: [
      "rgba(250, 204, 21, 0.55)", // consumption
      "#FFFFFF",                 // remaining
    ],
  };

  /* ===============================
     7️⃣ Render
  =============================== */
  return (
    <Card bg="neutral.900" p="4">
      <Text color="#aaa" fontSize="sm" fontWeight="500">
        Inventory Projection ({interval})
      </Text>

      <Box h="190px" mt="3" w="100%">
        <BarChart chartData={series} chartOptions={chartOptions} />
      </Box>
    </Card>
  );
}